<template>
  <div class="component">
    <router-view />

    <Stack
      class="stk-p-6"
      gap="4"
    >
      <Level id="entity-header">
        <LevelLeft>
          <Title size="7">
            People
          </Title>
        </LevelLeft>

        <LevelRight>
          <Buttons>
            <Button
              variant="primary"
              icon-after="fas fa-plus"
              :disabled="isLoading"
              size="3_5"
              @click="showAddModal = true"
            >
              <span>Add a Person</span>
            </Button>
          </Buttons>
        </LevelRight>
      </Level>

      <div>
        <Buttons
          class="filters"
          gap="6"
        >
          <form
            class="search-form stk-resize-3_5"
            @submit.prevent="handleSearch"
          >
            <input
              v-model="filters.search"
              type="search"
              class="stk-input search-input"
              placeholder="Search by name or email"
              :disabled="isLoading"
            >
            <Button
              variant="primary"
              :disabled="isLoading"
            >
              Search
            </Button>
          </form>

          <div class="options">
            <label class="option">
              <input
                v-model="filters.type"
                type="radio"
                :disabled="isLoading"
                value=""
              >
              All
            </label>
            <label class="option">
              <input
                v-model="filters.type"
                type="radio"
                :disabled="isLoading"
                value="Prospect"
              >
              <Badge
                resizable
                status="info"
              >Prospect</Badge>
            </label>
            <label class="option">
              <input
                v-model="filters.type"
                type="radio"
                :disabled="isLoading"
                value="Client"
              >
              <Badge
                resizable
                status="success"
              >Client</Badge>
            </label>
          </div>

          <div
            v-if="advisorStore.featureFlags.includes('Teams')"
            class="options"
          >
            <label class="option">
              <input
                v-model="filters.hideSharedWithMe"
                type="checkbox"
                :disabled="isLoading"
                :value="true"
              >
              Hide shared with me
            </label>
          </div>

          <div class="options">
            <label class="option">
              <input
                v-model="filters.onlyHouseholds"
                type="checkbox"
                :disabled="isLoading"
              >
              <span>
                Hide household accounts
              </span>
              <StakeTooltip
                label="Hides individual accounts that are already part of a household to reduce clutter. You can manage households by going to a person's detail page and clicking on the 'Groups' tab."
                position="bottom"
              >
                <Icon icon="fas fa-info-circle" />
              </StakeTooltip>
            </label>
          </div>

          <Button
            icon-before="fas fa-redo-alt"
            size="3_5"
            spacing="tight"
            :disabled="isLoading"
            @click="handleReset"
          >
            <span>Reset Filters</span>
          </Button>
        </Buttons>
      </div>
    </Stack>

    <div class="people-table">
      <PeopleTable
        :has-fetched="advisorStore.people.hasFetched"
        :loading="isLoading"
        :people="advisorStore.people"
        :columns="columns"
        :sort="advisorStore.people.sort"
        :dir="advisorStore.people.dir"
        @onPrevious="handlePrevious"
        @onNext="handleNext"
        @onPageClick="handlePageClick"
        @onReset="handleReset"
        @onColumnSort="handleColumnSort"
      />
    </div>

    <StakeModal v-model="showAddModal">
      <NewPersonModal
        @onClose="showAddModal = false"
        @onAdd="handleReset"
      />
    </StakeModal>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import NewPersonModal from '@frontend/ui/features/people/NewPersonModal.vue';
import PeopleTable from '@frontend/ui/features/people/PeopleTable.vue';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore.js';

import {
  Badge,
  Button,
  Buttons,
  Icon,
  Level,
  LevelLeft,
  LevelRight,
  Stack,
  StakeModal,
  StakeTooltip,
  Title,
} from '@stakeui';

const DEFAULT_FILTERS = Object.freeze({
  // Sorting
  dir: 'desc',
  sort: 'updated_at',
  // Filtering
  page: 1,
  search: '',
  type: '',
  hideSharedWithMe: false,
  onlyHouseholds: false,
});

const advisorStore = useAdvisorStore();
const showAddModal = ref(false);
const filters = ref({ ...DEFAULT_FILTERS });

const isLoading = computed(() => {
  return advisorStore.people.isFetching || !advisorStore.people.hasFetched;
});

const performSearch = () => {
  advisorStore.fetchPeople(filters.value);
};

onMounted(() => {
  if (advisorStore.people.hasFetched) {
    return;
  }

  performSearch();
});

const handlePrevious = () => {
  if (!advisorStore.people.hasPrevious) {
    return;
  }

  filters.value.page = advisorStore.people.page - 1;
  performSearch();
};

const handleNext = () => {
  if (!advisorStore.people.hasNext) {
    return;
  }

  filters.value.page = advisorStore.people.page + 1;
  performSearch();
};

const handlePageClick = page => {
  filters.value.page = page;
  performSearch();
};

const handleReset = () => {
  filters.value = { ...DEFAULT_FILTERS };
  performSearch();
};

const handleColumnSort = (sortable, columnId, defaultDir) => {
  if (!sortable) {
    return;
  }
  if (isLoading.value) {
    return;
  }

  let newDir = defaultDir;

  // We're clicking on the same column,
  // so we're just flipping the direction
  if (advisorStore.people.sort == columnId) {
    newDir = advisorStore.people.dir === 'asc' ? 'desc' : 'asc';
  }

  filters.value.sort = columnId;
  filters.value.dir = newDir;
  performSearch();
};

const handleSearch = () => {
  if (!filters.value.search) {
    return;
  }

  // Reset to the first page
  filters.value.page = 1;
  performSearch();
};

watch([
  () => filters.value.type,
  () => filters.value.hideSharedWithMe,
  () => filters.value.onlyHouseholds,
],
() => {
  filters.value.page = 1;
  performSearch();
},
);

const columns = ref([
  { id: 'name', label: 'Name', sortable: true, defaultDir: 'asc' },
  { id: 'entity_type', label: 'Type', sortable: true, defaultDir: 'asc' },
  { id: 'parents', label: 'Part of' },
  { id: 'children', label: 'Includes' },
  { id: 'owner', label: 'Owner' },
  { id: 'integrations', label: 'Linked To' },
  // { id: "alerts", label: "Alerts (Coming Soon)" }, // Example would be if portfolio risk is out of alignment with client risk.
  { id: 'created_at', label: 'Created', sortable: true, defaultDir: 'desc' },
  { id: 'updated_at', label: 'Updated', sortable: true, defaultDir: 'desc' },
]);
</script>

<style scoped>
.component {
  background-color: var(--stk-scheme-main);
}

.filters {
  row-gap: 0.25em;
}

.search-form {
  display: flex;
  align-items: center;
  gap: 0.75em;
}

.search-input {
  min-width: 16em;
}

.options,
.option {
  display: flex;
  align-items: center;
}

.options {
  gap: 0.5em;
}

.option {
  cursor: pointer;
  gap: 0.25em;
  font-size: 0.875em;
}
</style>
